import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';
import { toast } from 'react-toastify';

interface ProviderDocumentsGridProps {}

const ProviderDocumentsGrid: React.FC<ProviderDocumentsGridProps> = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const document = location.state?.document;
    
    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState<any[]>([]);
    const [filteredReports, setFilteredReports] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [filters, setFilters] = useState<Record<string, any[]>>({});
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 50;

    useEffect(() => {
        if (batchId && document) {
            loadReports();
        }
    }, [batchId, document]);

    const loadReports = async () => {
        try {
            setLoading(true);
            
            const cachedBatchData = location.state?.batchData;
            let batchData;
            
            if (cachedBatchData && cachedBatchData.reports) {
                batchData = cachedBatchData;
            } else {
                batchData = await recommendationBFFService.analyzeBatch(batchId!);
            }
            
            if (!batchData || !batchData.reports) {
                toast.error('Nenhum dado encontrado para este lote');
                return;
            }

            const documentReports = batchData.reports.filter((r: any) => r.document === document);
            
            if (documentReports.length === 0) {
                toast.warning('Nenhum processo encontrado para este documento');
                return;
            }

            setReports(documentReports);
            setFilteredReports(documentReports);
            generateColumns(documentReports);
            
        } catch (error) {
            console.error('Erro ao carregar reports:', error);
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const generateColumns = (reports: any[]) => {
        if (reports.length === 0) return;
        
        const allKeys = Object.keys(reports[0]);
        
        // Campos a excluir
        const excludeFields = [
            'document', 'nome', 'backend_pld_flag', 'backend_credit_flag',
            'backend_recommendation', 'backend_reasons', 'backend_risk_score', 
            'backend_analysis_id', 'backend_document_type',
            'status', 'statusFormatted', 'createdAt', 'updatedAt',
            'batchReportId', 'attempts', 'maxAttempts', 'events',
            'reportType', 'reportTypeId', 'analysis_status'
        ];
        
        // Coluna fixa: Recomendação
        const recommendationColumn = {
            id: 'recommendation',
            label: 'Recomendação',
            fixed: true,
            sortable: true,
            filterable: true
        };
        
        // Colunas dinâmicas
        const dynamicColumns = allKeys
            .filter(key => !excludeFields.includes(key))
            .map(key => ({
                id: key,
                label: formatColumnLabel(key),
                sortable: true,
                filterable: true
            }));
        
        setColumns([recommendationColumn, ...dynamicColumns]);
    };

    const formatColumnLabel = (key: string): string => {
        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    const getRecommendationBadge = (report: any) => {
        const creditFlag = report.backend_credit_flag;
        let recommendation = report.backend_recommendation;
        
        // Normalizar PT → EN
        if (recommendation) {
            const normalized = recommendation.toUpperCase();
            if (normalized === 'APROVADO') recommendation = 'APPROVED';
            else if (normalized === 'PENDENTE') recommendation = 'PENDING';
            else if (normalized === 'REPROVADO' || normalized === 'REJEITADO') recommendation = 'REJECTED';
        }
        
        // Prioridade: credit_flag > recommendation
        if (creditFlag) {
            return (
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    🎯 Sinalizado Crédito
                </span>
            );
        }
        
        const badges: Record<string, JSX.Element> = {
            'APPROVED': <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">✅ Aprovado</span>,
            'PENDING': <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">⏳ Pendente</span>,
            'REJECTED': <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">❌ Reprovado</span>
        };
        
        return badges[recommendation] || badges['PENDING'];
    };

    const formatCellValue = (value: any): string => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    const applyFilters = (reports: any[], activeFilters: Record<string, any[]>) => {
        return reports.filter(report => {
            return Object.entries(activeFilters).every(([columnId, values]) => {
                if (values.length === 0) return true;
                
                // Para a coluna de recomendação
                if (columnId === 'recommendation') {
                    const badge = getRecommendationText(report);
                    const match = values.includes(badge);
                    console.log('Filtro recommendation:', { badge, values, match, report: report.document });
                    return match;
                }
                
                // Para outras colunas
                return values.includes(report[columnId]);
            });
        });
    };

    const getRecommendationText = (report: any): string => {
        if (report.backend_credit_flag) return 'Sinalizado Crédito';
        if (report.backend_recommendation === 'SINALIZADO_CREDITO') return 'Sinalizado Crédito';
        if (report.backend_recommendation === 'APROVADO') return 'Aprovado';
        if (report.backend_recommendation === 'REPROVADO') return 'Reprovado';
        if (report.backend_recommendation === 'PENDENTE') return 'Pendente';
        return 'Pendente';
    };

    const handleFilterChange = (columnId: string, value: any, checked: boolean) => {
        setFilters(prev => {
            const columnFilters = prev[columnId] || [];
            
            if (checked) {
                return { ...prev, [columnId]: [...columnFilters, value] };
            } else {
                return { ...prev, [columnId]: columnFilters.filter(v => v !== value) };
            }
        });
    };

    const clearFilter = (columnId: string) => {
        setFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters[columnId];
            return newFilters;
        });
    };

    // Aplicar filtros
    useEffect(() => {
        const filtered = applyFilters(reports, filters);
        setFilteredReports(filtered);
        setCurrentPage(0); // Reset para primeira página
    }, [filters, reports]);

    // Paginação
    const paginatedReports = filteredReports.slice(
        currentPage * pageSize,
        (currentPage + 1) * pageSize
    );
    
    const totalPages = Math.ceil(filteredReports.length / pageSize);

    // Agregar todas as regras acionadas de todos os reports
    const aggregateRules = () => {
        const rulesMap = new Map<string, number>();
        
        reports.forEach(report => {
            if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
                report.backend_reasons.forEach((rule: string) => {
                    rulesMap.set(rule, (rulesMap.get(rule) || 0) + 1);
                });
            }
        });
        
        return Array.from(rulesMap.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([rule, count]) => ({ rule, count }));
    };

    const handleBack = () => {
        // Voltar com cache - passar batchData de volta
        const batchData = location.state?.batchData;
        navigate(`/recommendation-engine/recommendations/${batchId}`, {
            state: { batchData } // Passar cache de volta
        });
    };

    return (
        <PageForm
            pageTitle={`Processos - Documento ${document}`}
            apiVersion="Recommendation Engine - 1.0.0"
            isLoading={loading}
            breadcrumb={[
                { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                { label: 'Detalhes do Lote', url: '#' },
                { label: 'Processos', url: '#' }
            ]}
            controlSlot={<></>}
        >
            {/* Botão Voltar */}
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                >
                    <span>←</span>
                    <span>Voltar</span>
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                {/* Informações do Documento */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        📄 Informações do Documento
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Documento:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{document}</span>
                        </div>
                        <div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Total de Processos:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{filteredReports.length}</span>
                        </div>
                    </div>
                </div>

                {/* Dashboard: Resumo de Regras Acionadas */}
                {reports.length > 0 && aggregateRules().length > 0 && (
                    <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-700 dark:to-slate-600 p-6 rounded-lg border border-blue-200 dark:border-slate-500">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                            <span className="text-2xl">📊</span>
                            <span>Dashboard - Regras Acionadas</span>
                            <span className="ml-auto text-sm font-normal text-gray-600 dark:text-gray-300">
                                {aggregateRules().length} regra(s) única(s)
                            </span>
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {aggregateRules().map(({ rule, count }, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-slate-600 hover:shadow-md transition-shadow">
                                    <div className="flex items-start gap-3 flex-1">
                                        <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">•</span>
                                        <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">{rule}</span>
                                    </div>
                                    <span className="ml-3 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-semibold">
                                        {count}x
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Grid com Filtros */}
                <div 
                    className="overflow-auto" 
                    style={{ 
                        minHeight: '400px',
                        maxHeight: '600px'
                    }}
                >
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" style={{ width: 'max-content' }}>
                        <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
                            <tr>
                                {columns.map(column => (
                                    <ColumnHeader
                                        key={column.id}
                                        column={column}
                                        reports={reports}
                                        activeFilters={filters[column.id] || []}
                                        onFilterChange={(value, checked) => handleFilterChange(column.id, value, checked)}
                                        onClearFilter={() => clearFilter(column.id)}
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {paginatedReports.map((report, idx) => (
                                <tr key={idx}>
                                    {columns.map(column => (
                                        <td key={column.id} className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {column.id === 'recommendation' 
                                                ? getRecommendationBadge(report)
                                                : formatCellValue(report[column.id])
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, filteredReports.length)} de {filteredReports.length}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                className="px-3 py-1 bg-gray-200 dark:bg-slate-600 rounded disabled:opacity-50"
                            >
                                Anterior
                            </button>
                            <span className="px-3 py-1">
                                Página {currentPage + 1} de {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage === totalPages - 1}
                                className="px-3 py-1 bg-gray-200 dark:bg-slate-600 rounded disabled:opacity-50"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </PageForm>
    );
};

// Componente ColumnHeader com filtro
const ColumnHeader: React.FC<{
    column: any;
    reports: any[];
    activeFilters: any[];
    onFilterChange: (value: any, checked: boolean) => void;
    onClearFilter: () => void;
}> = ({ column, reports, activeFilters, onFilterChange, onClearFilter }) => {
    const [showFilter, setShowFilter] = useState(false);
    
    // Obter valores únicos para a coluna
    const uniqueValues = React.useMemo(() => {
        if (column.id === 'recommendation') {
            return ['Aprovado', 'Pendente', 'Reprovado', 'Sinalizado Crédito'];
        }
        
        const values = reports.map(r => r[column.id]).filter(v => v !== null && v !== undefined);
        return Array.from(new Set(values)).sort();
    }, [reports, column.id]);

    return (
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider relative">
            <div className="flex items-center justify-between gap-2">
                <span>{column.label}</span>
                {column.filterable && (
                    <button
                        onClick={() => setShowFilter(!showFilter)}
                        className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 ${
                            activeFilters.length > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                        }`}
                        title="Filtrar"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                    </button>
                )}
            </div>
            
            {showFilter && (
                <div className="absolute z-10 mt-2 w-64 bg-white dark:bg-slate-700 shadow-lg rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="max-h-64 overflow-y-auto mb-3">
                        {uniqueValues.map((value: any) => (
                            <label key={value} className="flex items-center gap-2 py-1 hover:bg-gray-50 dark:hover:bg-slate-600 px-2 rounded cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={activeFilters.includes(value)}
                                    onChange={(e) => onFilterChange(value, e.target.checked)}
                                    className="rounded"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-200">{String(value)}</span>
                            </label>
                        ))}
                    </div>
                    <div className="flex gap-2 border-t border-gray-200 dark:border-gray-600 pt-2">
                        <button
                            onClick={onClearFilter}
                            className="flex-1 px-3 py-1 text-sm bg-gray-200 dark:bg-slate-600 rounded hover:bg-gray-300 dark:hover:bg-slate-500"
                        >
                            Limpar
                        </button>
                        <button
                            onClick={() => setShowFilter(false)}
                            className="flex-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}
        </th>
    );
};

export default ProviderDocumentsGrid;
